<template>
	<div>
		<form @submit.prevent class="xs-12 p4">
			<!-- Text -->
			<div class="lh125 c1 mt2 mb4">
				{{ $t("invite_client_description") }}
			</div>

			<!-- Email -->
			<div class="xs-12 mb2">
				<j-base-input-text
					v-model="emailOfUserToBeInvited"
					:placeholder="$t('email')"
					name="email"
					v-validate="'email|required'"
					:data-vv-as="$t('email')"
					:status="errors.first('email') ? 'error' : null"
				/>
			</div>

			<!-- Policies -->
			<div class="xs-12 mt4">
				<div class="fw7 lh125 mb1">
					Select a policy that this client should see in the Client Portal
				</div>
				<!-- Fetching policies -->
				<div v-if="isCompanyPoliciesLoading" class="xs-12">Loading...</div>
				<!-- Display select after fecthed -->
				<j-base-select
					v-else
					v-model="policyId"
					name="policy"
					:options="policiesOfCompanyList"
					v-validate="'required'"
					:data-vv-as="$t('policy')"
					:status="errors.first('policy') ? 'error' : null"
				/>
			</div>

			<!-- Button -->
			<div class="flex xs-12 xs-center mt4">
				<j-button @click="handleInvite" :isLoading="isLoading" class="ml2">
					{{ $t("invite") }}
				</j-button>
			</div>
		</form>
	</div>
</template>

<script>
import JButton from "@/components/JButton/"
import JBaseInputText from "@/components/JBaseInputText/"
import JBaseSelect from "@/components/JBaseSelect/"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import dayjs from "dayjs"

const INVITE_ENTITY_TO_LOGIN = gql`
	mutation inviteEntityToLogin($clientId: String!, $input: inviteEntityInput!) {
		inviteEntityToLogin(clientId: $clientId, input: $input) {
			status
			errors
			createdStatus {
				id
			}
		}
	}
`

const POLICIES_OF_COMPANY = gql`
	query policiesOfCompany(
		$where: companyWhereInput
		$skip: Int
		$limit: Int
		$sort: sortInput
	) {
		companies(where: $where, skip: $skip, limit: $limit, sort: $sort) {
			list {
				__typename
				holderOf {
					createdAt
					id
					holder {
						name
					}
					issuerNumber
					status
					premium {
						formattedPrice
						appliedDiscounts {
							code
						}
					}
					product {
						name
						insurer {
							name
						}
						productId {
							type
							plan
							version
						}
					}
				}
			}
		}
	}
`

const ADD_TARGETTED_PERMISSIONS = gql`
	mutation addTargettedPermissions(
		$loginId: String!
		$inputCompany: addTargettedPermissionInput!
		$inputPolicy: addTargettedPermissionInput!
	) {
		company: addTargettedPermission(
			loginId: $loginId
			addTargettedPermissionInput: $inputCompany
		) {
			errors
			status
		}
		policy: addTargettedPermission(
			loginId: $loginId
			addTargettedPermissionInput: $inputPolicy
		) {
			errors
			status
		}
	}
`

export default {
	components: { JButton, JBaseInputText, JBaseSelect },
	props: {
		entity: {
			type: Object,
			default: () => ({})
		}
	},

	data() {
		return {
			clientId: "coverPortal",
			// clientId: "covergo_crm",

			isLoading: false,
			isCompanyPoliciesLoading: false,

			// When inviting user through modal,
			// here we store info about the user that should be invited
			emailOfUserToBeInvited: "",

			// For adding targeted permission, user has to select policy to share
			policyId: "",

			// Policies of this company will be fetched here
			policiesOfCompanyList: []
		}
	},

	computed: {
		// Entity Id
		id() {
			return this?.entity?.id
		}
	},

	methods: {
		async handleInvite() {
			// Validate
			if (!this.id) return
			const isValid = await this.$validator.validate()
			if (!isValid) return

			// ------------------------------------
			// 1: Invite user
			// ------------------------------------
			this.isLoading = true

			const res = await api({
				query: INVITE_ENTITY_TO_LOGIN,
				variables: {
					clientId: this.clientId,
					input: {
						entityId: this.id, // id of internal
						email: this.emailOfUserToBeInvited,
						username: this.emailOfUserToBeInvited
					}
				}
			})

			// Check errors
			if (res?.data?.inviteEntityToLogin?.status !== "success") {
				if (res.errors)
					this.$store.dispatch("showError", res?.errors?.[0]?.message)
				this.isLoading = false
				return
			}

			// ------------------------------------
			// 2: add targeted permissions for a selected policy and for company itself
			// ------------------------------------
			const loginId = res?.data?.inviteEntityToLogin?.createdStatus?.id

			const res2 = await api({
				query: ADD_TARGETTED_PERMISSIONS,
				variables: {
					loginId,
					inputCompany: {
						type: "readCompanies",
						value: this.id
					},
					inputPolicy: {
						type: "readPolicies",
						value: this.policyId
					}
				}
			})

			// Check errors
			if (
				res2?.data?.company?.status !== "success" ||
				res2?.data?.policy?.status !== "success"
			) {
				this.$store.dispatch(
					"showError",
					res2?.data?.company?.errors?.[0] ||
						res2?.data?.poicy?.errors?.[0] ||
						this.$t("errors.general")
				)
				this.isLoading = false
				return
			}

			// Emit
			// Show message
			this.$store.dispatch("addMessage", {
				message: { type: "success", text: this.$t("user_invited") }
			})

			this.$emit("invite:finished")
			this.isLoading = false
		},

		async fetchPoliciesOfCompany() {
			this.isCompanyPoliciesLoading = true
			const res = await api({
				query: POLICIES_OF_COMPANY,
				variables: {
					where: { id: this.id }
				}
			})
			this.isCompanyPoliciesLoading = false

			// Massage data for options
			this.policiesOfCompanyList = res?.data?.companies?.list?.[0]?.holderOf?.map(
				item => {
					const dateFormat = this.$store.state.settings.dateFormat
					const productName = item?.product?.name || ""
					const createdAt = item.createdAt
						? dayjs(item.createdAt).format(dateFormat)
						: ""

					return {
						name: `${productName} (${createdAt})`,
						value: item.id
					}
				}
			)
		}
	},

	created() {
		// Populate email from this.entity data
		// We are assuming that the email for username for login will be the same
		this.emailOfUserToBeInvited = this?.entity?.contacts?.find(
			item => item?.type === "email"
		)?.value

		// Fetch policies of company list
		this.fetchPoliciesOfCompany()
	}
}
</script>
